//#region IMPORTS
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { storeFormatSelectors } from "../../features/storeFormats/storeFormatSlice";
import { useAppSelector } from "../../store/configureStore";
import { Fragment } from "react";
import { Store } from "../../models/store";
import StoresTableRowActionReadUpdate from "./StoresTableRowActionReadUpdate";
import StoresTableRowActionGoToInstoreOrders from "./StoresTableRowActionGoToInstoreOrders";
import dateHelperWeirdFromBackend from "../../helpers/dateHelperWeirdFromBackend";
//#endregion

//#region INTERFACE
interface Props {
  isHQTeam: boolean;
  isPicker: boolean;
  isAdmin: boolean;
  store: Store;
}
//#endregion

export default function StoresTableRow({
  isHQTeam = false,
  isPicker = false,
  isAdmin = false,
  store,
}: Props) {
  const storeFormatsToSelect = useAppSelector(storeFormatSelectors.selectAll);
  const storesWithOrderedInstore = useAppSelector(
    (state) => state.stores.storesWithOrderedInstore,
  );

  // Show the "go to orders" door button when this store has a non-Week
  // (Opening/Relocation) instore order in an active status (see DOOR_STATUSES in
  // storesSlice — Ordered→Delivered; not Pending/Archived/Cancelled). Roles:
  // picker/admin/HQ — the ones that can open the store manager.
  const hasOrderedInstore =
    (isAdmin || isPicker || isHQTeam) &&
    storesWithOrderedInstore.includes(store.external_store_id);

  //#region RENDER
  return (
    storeFormatsToSelect && (
      <Fragment>
        <tr className="border-t border-slate-300 h-10">
          {/* Actions */}
          <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
            <div className="flex items-center gap-1.5">
              <StoresTableRowActionReadUpdate
                isHQTeam={isHQTeam}
                isPicker={isPicker}
                isAdmin={isAdmin}
                store={store}
              />
              {hasOrderedInstore && (
                <StoresTableRowActionGoToInstoreOrders store={store} />
              )}
            </div>
          </td>

          {/* Active */}
          <td className="whitespace-nowrap px-2 text-left text-xs text-gray-900">
            {store.active ? (
              <CheckCircleIcon className="h-4 w-4 text-purple-500" />
            ) : (
              <XCircleIcon className="h-4 w-4 text-gray-400" />
            )}
          </td>

          {/* Store number */}
          <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
            {store.external_store_id}
          </td>

          {/* Store name */}
          <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
            {store.store_name}
          </td>

          {/* Street name */}
          <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500">
            {store.address_1}
          </td>

          {/* Postal code */}
          <td className="whitespace-nowrap px-2 text-left text-xs text-gray-900">
            {store.postal_code}
          </td>

          {/* City */}
          <td className="whitespace-nowrap px-2 text-left text-xs text-gray-900">
            {store.city}
          </td>

          {/* Country */}
          <td className="whitespace-nowrap px-2 text-left text-xs text-gray-900">
            {store.country_code}
          </td>

          {/* Language */}
          <td className="whitespace-nowrap px-2 text-left text-xs text-gray-900">
            {store.language}
          </td>

          {/* Updated at */}
          <td className="whitespace-nowrap px-2 text-left text-xs text-gray-900">
            {store.updated_at}
          </td>

          {/* Openings date */}
          <td className="whitespace-nowrap px-2 text-left text-xs text-gray-900">
            {dateHelperWeirdFromBackend(store.opening_date)}
          </td>

          {/* Storeformat */}
          <td className="whitespace-nowrap w-52 px-2 text-left text-xs text-gray-900">
            <div className="inline-flex items-center rounded-full bg-gray-200 px-2 text-xs font-medium text-gray-900">
              {store.store_format ? store.store_format.name : "no format"}
            </div>
          </td>
        </tr>
      </Fragment>
    )
  );
  //#endregion
}
